// Multi-manager buyer teams (WS4) — one team, at /buyers/teams/:id.
//
// NOT an entity detail page in the Layout Standard's sense: a buyer team is not
// one of the entities that standard scopes (§2), it is registered with none of
// the cross-cutting registries (Notes / Tasks / Documents / Calendar /
// Messages / Communications / Activity), and inventing those tabs here would
// mean six strips that render nothing. What it borrows from the standard is
// everything that DOES apply — the shared tab bar, one strip and no nested one
// (§6.5), and a header that is chrome rather than a workflow surface: the two
// controls it carries edit and archive the record you are looking at, which is
// data maintenance, and every workflow action lives in the tab that owns it.

import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  BarChart3,
  ChevronLeft,
  Globe,
  ListChecks,
  Megaphone,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { extractErrorMessage } from "@/api/client";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  Card,
  OverflowMenu,
  Skeleton,
  StatusBadge,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { useTeamBoard } from "@/hooks/buyers/useBuyerReports";
import { useBuyerTeam, useSetBuyerTeamArchived } from "@/hooks/buyers/useBuyerTeams";
import { useAuth } from "@/hooks/useAuth";
import {
  BUYER_TEAMS_ARCHIVE_PERMISSION,
  BUYER_TEAMS_MANAGE_PERMISSION,
  TeamEditModal,
} from "./BuyerTeamsPage";
import { isNotFoundError, QueryErrorState } from "./queryStates";
import { AdvertisingTab } from "./teams/AdvertisingTab";
import { AllocationTab } from "./teams/AllocationTab";
import { IntakeSitesTab } from "./teams/IntakeSitesTab";
import { MembersTab } from "./teams/MembersTab";
import { PerformanceTab, teamBoardPath } from "./teams/PerformanceTab";
import { QueueTab } from "./teams/QueueTab";

type TabKey =
  | "members"
  | "queue"
  | "intake-sites"
  | "allocation"
  | "advertising"
  | "performance";

/**
 * The strip, in display order — the roster first because it is the only tab
 * that both reads and writes this record, then the team's work, then the two
 * surfaces it hands off to, then the readout.
 */
const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "members", label: "Members", icon: Users },
  { key: "queue", label: "Queue", icon: ListChecks },
  { key: "intake-sites", label: "Intake sites", icon: Globe, shortLabel: "Intake" },
  { key: "allocation", label: "Allocation", icon: SlidersHorizontal },
  { key: "advertising", label: "Advertising", icon: Megaphone },
  { key: "performance", label: "Performance", icon: BarChart3 },
];

const TAB_KEYS = new Set<string>(TAB_ITEMS.map((t) => t.key));

export function BuyerTeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const toast = useToast();
  // The permission is the DOOR (F1 holds it, §3.3); whether THIS team is one
  // the caller may edit is the record's own server-resolved `canManage`, and
  // archive / unarchive stay all-teams-only. Resolved below once the record
  // is in hand — hiding is never the gate, but a control that would 403 must
  // not render.
  const canManageTeams = hasPermission(BUYER_TEAMS_MANAGE_PERMISSION);
  const canArchiveTeams = canManageTeams && hasPermission(BUYER_TEAMS_ARCHIVE_PERMISSION);

  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  // Parsed at read time rather than corrected in an effect — an effect that
  // rewrites the query string re-keys every read on the page and fires a
  // second round of requests on first paint.
  const tab: TabKey = raw && TAB_KEYS.has(raw) ? (raw as TabKey) : "members";

  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const team = useBuyerTeam(id);
  const setArchived = useSetBuyerTeamArchived();
  // D13 — the board read is a second request and is scoped server-side (a
  // one-team caller reading another team's board is a 403), so its failure
  // renders NOTHING: the count is a courtesy on this header, not a claim the
  // page depends on.
  const board = useTeamBoard(team.data?.id);
  const staleCount = board.data
    ? board.data.specialists.reduce((n, s) => n + s.staleRows.length, 0)
    : null;

  function selectTab(next: TabKey) {
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    // Paging belongs to the tab that paged. Carrying `page=3` from the Queue
    // tab onto another one is harmless today and a trap the moment a second
    // tab pages, so it is dropped on every tab change.
    nextParams.delete("page");
    setParams(nextParams, { replace: true });
  }

  function confirmArchive() {
    if (!team.data) return;
    const archive = team.data.status !== "Archived";
    setArchived.mutate(
      { id: team.data.id, archived: archive },
      {
        onSuccess: () => {
          setArchiving(false);
          toast.success(`"${team.data!.name}" ${archive ? "archived" : "unarchived"}.`);
        },
        onError: (e) => {
          setArchiving(false);
          toast.error(extractErrorMessage(e));
        },
      },
    );
  }

  if (!id) return null;

  if (team.isLoading) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="buyer-team-loading">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (team.isError || !team.data) {
    // A 404 is a fact about the record; anything else is a fact about the
    // REQUEST, and the two must not be rendered as the same sentence.
    return isNotFoundError(team.error) ? (
      <Card data-testid="buyer-team-missing">
        <h1 className="font-display text-h3 text-onyx m-0 mb-space-2">Team not found</h1>
        <p className="text-small text-gray-mid m-0 mb-space-3">
          It may have been removed.
        </p>
        <Link to="/buyers/teams" className="text-gold-shadow hover:underline">
          Back to buyer teams
        </Link>
      </Card>
    ) : (
      <QueryErrorState
        what="this team"
        onRetry={() => void team.refetch()}
        retrying={team.isFetching}
      />
    );
  }

  const t = team.data;
  const archived = t.status === "Archived";
  const canManage = canManageTeams && t.canManage === true;
  const canArchive = canArchiveTeams && t.canManage === true;
  const headerActions = [
    ...(canManage ? [{ label: "Edit team", onSelect: () => setEditing(true) }] : []),
    ...(canArchive
      ? [
          archived
            ? { label: "Unarchive", onSelect: () => setArchiving(true) }
            : { label: "Archive", destructive: true, onSelect: () => setArchiving(true) },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-detail">
      <div>
        <Link
          to="/buyers/teams"
          className="inline-flex items-center gap-1 text-caption text-gray-mid hover:text-onyx no-underline"
          data-testid="buyer-team-back-link"
        >
          <ChevronLeft size={14} aria-hidden /> Buyer teams
        </Link>
        <PageHeader
          eyebrow="Buyers"
          title={
            <span className="inline-flex items-baseline gap-space-3 flex-wrap">
              {t.name}
              {archived && <StatusBadge tone="muted">Archived</StatusBadge>}
            </span>
          }
          description={
            <>
              {t.managerDisplayName ? `Manager: ${t.managerDisplayName}` : "No manager assigned"}
              {" · Created "}
              <Timestamp value={t.createdAt} absolute />
              {t.description ? ` — ${t.description}` : ""}
              {/* D13 — stale buyers across the roster, from the board (its
                  window is the team's own StaleContactDays). Only rendered
                  once the board answered and there is something to attend to. */}
              {staleCount != null && staleCount > 0 && (
                <>
                  {" · "}
                  <Link
                    to={teamBoardPath(t.id)}
                    className="text-status-warning-ink hover:underline"
                    data-testid="buyer-team-stale-count"
                  >
                    {staleCount} stale buyer{staleCount === 1 ? "" : "s"} need
                    {staleCount === 1 ? "s" : ""} attention
                  </Link>
                </>
              )}
            </>
          }
          className="mb-0"
          actions={
            // A "⋯" that opens onto nothing is worse than no "⋯".
            headerActions.length > 0 ? (
              <OverflowMenu
                label={`Actions for ${t.name}`}
                data-testid="buyer-team-actions"
                items={headerActions}
              />
            ) : undefined
          }
        />
      </div>

      <EntityTabBar<TabKey>
        ariaLabel="Buyer team sections"
        testIdPrefix="buyer-team"
        items={TAB_ITEMS}
        value={tab}
        onChange={selectTab}
        showLabels
      />

      {tab === "members" && <MembersTab team={t} canManage={canManage} />}
      {tab === "queue" && <QueueTab team={t} />}
      {tab === "intake-sites" && <IntakeSitesTab team={t} />}
      {tab === "allocation" && <AllocationTab team={t} />}
      {tab === "advertising" && <AdvertisingTab team={t} />}
      {tab === "performance" && <PerformanceTab team={t} />}

      {editing && (
        <TeamEditModal
          team={t}
          onClose={() => setEditing(false)}
          onSaved={(name) => {
            setEditing(false);
            toast.success(`Team "${name}" updated.`);
          }}
        />
      )}

      {archiving && (
        <ConfirmModal
          open
          title={archived ? "Unarchive team?" : "Archive team?"}
          message={
            archived ? (
              <>
                Unarchive <strong>{t.name}</strong>? It returns to the picker and can take
                members again.
              </>
            ) : (
              <>
                Archive <strong>{t.name}</strong>? It keeps its members, its leads and its
                history — it drops out of the picker and stops accepting new members.
              </>
            )
          }
          confirmLabel={archived ? "Unarchive" : "Archive"}
          destructive={!archived}
          onConfirm={confirmArchive}
          onCancel={() => setArchiving(false)}
        />
      )}
    </div>
  );
}
